(() => {
  'use strict';

  function normalizeTimezoneOffset(value) {
    if (!value) return '+00:00';

    const cleaned = value.trim();

    if (/^[+-]\d{4}$/.test(cleaned)) {
      return `${cleaned.slice(0, 3)}:${cleaned.slice(3)}`;
    }

    if (/^[+-]\d{2}:\d{2}$/.test(cleaned)) {
      return cleaned;
    }

    return '+00:00';
  }

  function initCountdown(block) {
    if (!block || block.dataset.countdownInitialized === 'true') return;

    block.dataset.countdownInitialized = 'true';

    const date = block.dataset.endDate;
    const time = block.dataset.endTime || '23:59';
    const offset = normalizeTimezoneOffset(block.dataset.endOffset);

    if (!date) {
      block.style.display = 'none';
      return;
    }

    const endDate = new Date(`${date}T${time}:00${offset}`);

    if (Number.isNaN(endDate.getTime())) {
      block.style.display = 'none';
      return;
    }

    const daysElement = block.querySelector('[data-countdown-days]');
    const hoursElement = block.querySelector('[data-countdown-hours]');
    const minutesElement = block.querySelector('[data-countdown-minutes]');
    const daysShortElement = block.querySelector('[data-countdown-days-short]');
    const hoursShortElement = block.querySelector('[data-countdown-hours-short]');
    const minutesShortElement = block.querySelector('[data-countdown-minutes-short]');

    if (!daysElement || !hoursElement || !minutesElement) return;

    let timer = null;

    function updateCountdown() {
      const remaining = endDate.getTime() - Date.now();

      if (remaining <= 0) {
        block.style.display = 'none';

        if (timer) {
          window.clearInterval(timer);
          timer = null;
        }

        return;
      }

      const totalMinutes = Math.floor(remaining / 60000);
      const days = Math.floor(totalMinutes / 1440);
      const hours = Math.floor((totalMinutes % 1440) / 60);
      const minutes = totalMinutes % 60;

      daysElement.textContent = String(days);
      hoursElement.textContent = String(hours);
      minutesElement.textContent = String(minutes);

      if (daysShortElement) {
        daysShortElement.textContent = String(days);
      }

      if (hoursShortElement) {
        hoursShortElement.textContent = String(hours);
      }

      if (minutesShortElement) {
        minutesShortElement.textContent = String(minutes);
      }
    }

    updateCountdown();

    timer = window.setInterval(updateCountdown, 30000);
  }

  function getSelectedOptions(productInfo) {
    const selectedOptions = [];

    const fieldsets = productInfo.querySelectorAll(
      'fieldset.product-form__input'
    );

    if (fieldsets.length) {
      fieldsets.forEach((fieldset) => {
        const checkedInput = fieldset.querySelector(
          'input[type="radio"]:checked'
        );

        if (checkedInput) {
          selectedOptions.push(checkedInput.value.trim());
        }
      });

      return selectedOptions;
    }

    const selects = productInfo.querySelectorAll(
      'select[name^="options["]'
    );

    selects.forEach((select) => {
      selectedOptions.push(select.value.trim());
    });

    return selectedOptions;
  }

  function getSelectedVariant(productInfo, sectionId) {
    const variantDataElement = document.getElementById(
      `variant-data-${sectionId}`
    );

    if (!variantDataElement) return null;

    let variants = [];

    try {
      variants = JSON.parse(variantDataElement.textContent);
    } catch (error) {
      console.warn('Unable to parse product variant data.', error);
      return null;
    }

    const selectedOptions = getSelectedOptions(productInfo);

    if (!selectedOptions.length) return null;

    return (
      variants.find((variant) => {
        return selectedOptions.every((value, index) => {
          const optionKey = `option${index + 1}`;
          return variant[optionKey] === value;
        });
      }) || null
    );
  }

  function initProductInfo(productInfo) {
    if (!productInfo || productInfo.dataset.digitalPdpInitialized === 'true') {
      return;
    }

    productInfo.dataset.digitalPdpInitialized = 'true';

    const sectionId = productInfo.dataset.sectionId;

    if (!sectionId) return;

    const addToCartButton = productInfo.querySelector('#add-to-cart');
    const paperbackButton = productInfo.querySelector('#amazon-buy-button');

    const digitalNoticeArea = productInfo.querySelector('#digital-notice-area');
    const formatNoticeBar = productInfo.querySelector('#format-notice-bar');

    const digitalHelpPill = productInfo.querySelector('#digital-help-pill');
    const digitalHelpPillLink = productInfo.querySelector(
      '#digital-help-pill-link'
    );
    const digitalHelpPillText = productInfo.querySelector(
      '#digital-help-pill-text'
    );
    const digitalHelpPillArrow = productInfo.querySelector(
      '#digital-help-pill-arrow'
    );

    function updateMainProductButtons() {
      const selectedVariant = getSelectedVariant(productInfo, sectionId);

      const isPaperback = selectedVariant
        ? selectedVariant.requires_shipping === true
        : false;

      const digitalBullets = productInfo.querySelector('#bullets-digital');
      const paperbackBullets = productInfo.querySelector('#bullets-paper');

      const paperbackAvailable =
        paperbackButton?.dataset.storeAvailable === 'true';

      const isWarning = isPaperback && !paperbackAvailable;

      if (digitalBullets) {
        digitalBullets.hidden = isPaperback;
        digitalBullets.style.display = isPaperback ? 'none' : '';
      }

      if (paperbackBullets) {
        paperbackBullets.hidden = !isPaperback;
        paperbackBullets.style.display = isPaperback ? '' : 'none';
      }

      if (addToCartButton) {
        addToCartButton.style.display = isPaperback ? 'none' : '';
      }

      if (paperbackButton) {
        paperbackButton.style.display = isPaperback ? 'inline-flex' : 'none';
      }

      if (digitalNoticeArea) {
        digitalNoticeArea.style.display = isPaperback ? 'none' : '';
      }

      if (formatNoticeBar) {
        formatNoticeBar.style.display = '';

        if (isWarning) {
          formatNoticeBar.textContent =
            productInfo.dataset.paperUnavailableNotice || '';
        } else if (isPaperback) {
          formatNoticeBar.textContent =
            productInfo.dataset.paperNotice || '';
        } else {
          formatNoticeBar.textContent =
            productInfo.dataset.digitalNotice || '';
        }

        formatNoticeBar.classList.remove(
          'all-inclusive-banner--normal',
          'all-inclusive-banner--warning'
        );

        formatNoticeBar.classList.add(
          isWarning
            ? 'all-inclusive-banner--warning'
            : 'all-inclusive-banner--normal'
        );
      }

      if (
        digitalHelpPill &&
        digitalHelpPillLink &&
        digitalHelpPillText &&
        digitalHelpPillArrow
      ) {
        digitalHelpPill.style.display = '';

        if (isPaperback) {
          digitalHelpPillText.textContent =
            productInfo.dataset.paperHelp || '';

          digitalHelpPillLink.removeAttribute('href');
          digitalHelpPillLink.style.cursor = 'default';
          digitalHelpPillArrow.style.display = 'none';
        } else {
          digitalHelpPillText.textContent =
            productInfo.dataset.digitalHelp || '';

          const helpUrl = productInfo.dataset.digitalHelpUrl || '';

          if (helpUrl) {
            digitalHelpPillLink.setAttribute('href', helpUrl);
          } else {
            digitalHelpPillLink.removeAttribute('href');
          }

          digitalHelpPillLink.style.cursor = '';
          digitalHelpPillArrow.style.display = '';
        }
      }
    }

    if (paperbackButton) {
      paperbackButton.addEventListener('click', () => {
        if (paperbackButton.disabled) return;

        const activePaperbackLink = productInfo.querySelector(
          '#official-price-paperback a[href]'
        );

        const url =
          activePaperbackLink?.getAttribute('href') ||
          paperbackButton.dataset.storeUrl;

        if (url) {
          window.open(url, '_blank', 'noopener');
        }
      });
    }

    productInfo.addEventListener('change', () => {
      window.setTimeout(updateMainProductButtons, 50);
    });

    updateMainProductButtons();
  }

  function initDigitalProductPage(root = document) {
    root
      .querySelectorAll('[data-main-product-digital]')
      .forEach(initProductInfo);

    root
      .querySelectorAll('[data-offer-countdown]')
      .forEach(initCountdown);
  }

  function initialize() {
    initDigitalProductPage(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initDigitalProductPage(event.target);
  });
})();